// Cartão de tarefa da lista: título, prazo (vermelho se atrasada), estimativa, tempo gasto,
// menu (editar/excluir) e "Iniciar" (sempre visível na próxima da fila). Ocorrência de
// tarefa recorrente: marcada em azul, com o ícone de repetir (ASM-114). Descrição abre e fecha (US-098).
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isOverdue, type Task } from '../domain/task';
import { elapsedSeconds } from '../domain/timer';
import { formatDue, formatMinutes, formatSpent } from '../i18n/format';
import { useAppState, useStore } from '../state/AppStore';
import { EditTaskDialog, ConfirmDialog } from './EditTaskDialog';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';
import { TaskDescription } from './TaskDescription';

interface Props {
  task: Task;
  index: number;
  now: number;
  isNext: boolean;
  canEdit: boolean;
  /** seção e posição para o arraste (US-007) */
  drag?: { section: string; index: number };
}

export function TaskRow({ task, index, now, isNext, canEdit, drag }: Props) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const sortable = useSortable({ id: task.id, data: drag, disabled: !canEdit || !drag });
  // a linha inteira é a alça de arraste; o papel de "botão" do dnd-kit é tirado para
  // não confundir com os botões de dentro dela
  const { role: _role, 'aria-pressed': _pressed, ...dragAttributes } = sortable.attributes;
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const timeFormat = useAppState((s) => s.profile?.time_format ?? '24h');
  const overdue = isOverdue(task, new Date(now));
  const paused = task.status === 'paused';

  return (
    <li
      ref={(el) => {
        sortable.setNodeRef(el);
        // só a própria linha inicia o arraste pelo teclado (não um campo ou botão dentro dela)
        sortable.setActivatorNodeRef(el);
      }}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      {...dragAttributes}
      {...sortable.listeners}
      aria-roledescription={t('drag.roleDescription')}
      className={cls('task', overdue && 'task--overdue', paused && 'task--paused', isNext && 'task--next', task.recurring_id && 'task--recurring', sortable.isDragging && 'task--dragging', canEdit && drag && 'task--draggable')}
      data-testid="task-row"
      data-task-id={task.id}
      data-overdue={overdue || undefined}
      data-recurring={task.recurring_id ? true : undefined}
    >
      <div className="task__top">
        <span className="task__num">{index}</span>
        {task.recurring_id && (
          <>
            <Icon name="repeat" size={14} className="task__repeat" />
            <span className="sr-only">{t('recurring.badge')}</span>
          </>
        )}
        <span className="task__title" title={task.title}>
          {task.title}
        </span>
        <div className="task__side">
          {task.due_date && (
            <span className={cls('due', overdue && 'due--overdue')}>
              <Icon name="clock" size={13} />
              {formatDue(task.due_date, task.due_time, new Date(now), i18n.language, t, timeFormat)}
            </span>
          )}
          <div className="task__actions">
            <button type="button" className="icon-btn icon-btn--sm" aria-label={t('task.menu')} aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <Icon name="more" size={16} />
            </button>
            <button
              type="button"
              className="btn btn--start"
              disabled={!canEdit}
              onClick={() => void store.start(task.id)}
            >
              <Icon name="play" size={12} filled />
              {paused ? t('task.resume') : t('task.start')}
            </button>
          </div>
          {menu && (
            <div className="menu" role="menu">
              <button
                type="button"
                role="menuitem"
                disabled={!canEdit}
                onClick={() => {
                  setMenu(false);
                  setEditing(true);
                }}
              >
                <Icon name="edit" size={14} />
                {t('task.edit')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="menu__danger"
                disabled={!canEdit}
                onClick={() => {
                  setMenu(false);
                  setConfirmDelete(true);
                }}
              >
                <Icon name="trash" size={14} />
                {t('task.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
      {task.description && <TaskDescription taskId={task.id} text={task.description} place="list" />}
      <div className="task__meta">
        <span>
          <Icon name="timer" size={13} />
          {task.estimate_minutes ? formatMinutes(task.estimate_minutes, t) : t('task.noEstimate')}
        </span>
        <span>{paused ? t('task.pausedAt', { time: formatSpent(elapsedSeconds(task, now), t) }) : formatSpent(elapsedSeconds(task, now), t)}</span>
      </div>
      {overdue && <span className="sr-only">{t('task.overdue')}</span>}

      {editing && <EditTaskDialog task={task} onClose={() => setEditing(false)} />}
      {confirmDelete && (
        <ConfirmDialog
          title={t('task.deleteTitle')}
          body={t('task.deleteBody', { title: task.title })}
          confirmLabel={t('task.delete')}
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setConfirmDelete(false);
            await store.deleteTask(task.id);
          }}
        />
      )}
    </li>
  );
}
